import { PrismicRichText, SliceComponentProps } from "@prismicio/react";
import { Slice } from "@prismicio/types";
import clsx from "clsx";
import { GatsbyImage } from "gatsby-plugin-image";
import React, { useRef, useState } from "react";
import Slider from "react-slick";
import {
  CapabilitiesSliceFragment,
  PrismicImageFieldBase,
} from "../../types/graphql";
import { IPageContext } from "../../types/types";
import { htmlSerializer } from "../../utils/htmlSerializer";
import { SharedSliceType } from "../shared-slice/shared-slice";
import { SliderArrow, SliderNav } from "./capabilities.styled";
import * as FA from "../../styles/fontawesome";

export type ICapabilitiesProps = Slice<
  SharedSliceType,
  CapabilitiesSliceFragment["primary"],
  CapabilitiesSliceFragment["items"][number]
> & { type: SharedSliceType.capabilities };

const Capabilities = ({
  slice,
}: SliceComponentProps<ICapabilitiesProps, IPageContext>) => {
  console.log("[Capabilities] slice", slice);
  const { primary, items } = slice;

  const [image, setImage] = useState<PrismicImageFieldBase | undefined>();
  const leftCol = items.slice(0, 4);
  const rightCol = items.slice(4, 8);
  const bottomRow = items.slice(8);

  const handleMouseEnter = (image: PrismicImageFieldBase) => {
    setImage(image);
  };

  const handleMouseLeave = () => {
    setImage(undefined);
  };

  const [currentSlide, setCurrentSlide] = useState(0);

  // Slick Slider
  const sliderRef = useRef<Slider>(null);
  const [sliderInitialized, setSliderInitialized] = useState(false);
  const settings = {
    arrows: false,
    dots: false,
    dotsClass:
      "tabs lg:border-b-1 lg:!grid lg:!grid-cols-4 lg:!gap-4 [&>li]:text-center [&>li]:self-center [&>li]:h-full [&>li]:!grid [&>li]:!gridl-cols-1 [&>li]:content-center [&>.slick-active]:text-[#0E6AED] [&>li_a_h6]:hover:!text-[#0E6AED] [&>.slick-active]:border-b-4 [&>.slick-active]:border-[#0E6AED] [&>.slick-active_a_h6]:!text-[#0E6AED] [&>.slick-active_a]:pb-1",
    fade: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    waitForAnimate: false,
    // customPaging: function (i: number) {
    //   return (
    //     <a className="uppercase font-semibold block py-2 px-2 cursor-pointer">
    //       <PrismicRichText
    //         field={body[i]?.primary?.heading.richText}
    //         components={htmlSerializer}
    //       />
    //     </a>
    //   );
    // },
    afterChange: (current: any) => {
      setCurrentSlide(current);
    },
    onInit: () => setSliderInitialized(true),
  };

  return (
    (slice && (
      <section className="slice-section">
        <div className="container">
          {primary.primary_heading && (
            <h2 className="text-center">
              <PrismicRichText
                field={primary.primary_heading.richText}
                components={htmlSerializer}
              />
            </h2>
          )}
          <div className="lg:hidden">
            <Slider
              ref={sliderRef}
              className="!flex !flex-col !flex-col-reverse my-8"
              {...settings}
            >
              {sliderInitialized &&
                items?.map((item) => {
                  return (
                    <div className="mx-auto">
                      <div className="w-[350px] h-[350px] max-w-[350px] md:w-[420px] md:h-[420px] md:max-w-[420px] rounded-full border-1 border-[#0E6AED] mx-auto relative z-10 p-3.5">
                        <div className="w-full h-full bg-[#2437BF] rounded-full border-18 border-[#2437BF]">
                          {item?.image && (
                            <GatsbyImage
                              image={item.image?.gatsbyImageData}
                              alt={item.image?.alt || "Xometry Resources"}
                              loading="lazy"
                              className="!absolute top-8 left-8 right-8 bottom-8 rounded-full overflow-hidden [&_img]:!object-cover [&_img]:!aspect-square"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </Slider>

            <SliderNav>
              <SliderArrow onClick={() => sliderRef.current?.slickPrev()}>
                <i css={FA.AngleLeftRegular} />
              </SliderArrow>
              <div className="grow text-center text-[#476175] text-lg">
                {items[currentSlide].cta_text}
              </div>
              <SliderArrow onClick={() => sliderRef.current?.slickNext()}>
                <i css={FA.AngleRightRegular} />
              </SliderArrow>
            </SliderNav>
          </div>
          <div className="hidden lg:!block relative">
            <div className="!grid !grid-cols-12 relative">
              <div className="!col-span-3">
                <ul className="!grid text-center min-h-[460px]">
                  {leftCol &&
                    leftCol.map((item, index) => (
                      <li
                        key={index}
                        className={clsx([
                          "!grid h-[115px] w-full grow",
                          item?.css_class,
                        ])}
                      >
                        <a
                          className="text-lg underline self-center rounded-full text-[#476175] hover:!bg-[#13BF87] hover:text-white py-2 px-8 w-full"
                          href={item.cta_link?.url || ""}
                          onMouseEnter={() =>
                            item.image && handleMouseEnter(item.image)
                          }
                          onMouseLeave={handleMouseLeave}
                        >
                          {item.cta_text}
                        </a>
                        <span className="pointer absolute">
                          <span className="dot rounded-full block w-3.5 h-3.5 bg-[#0E6AED]"></span>
                          <span className="line absolute top-[7px] left-[7px] inline-block h-1 w-[330px] border-t-1 border-[#0E6AED] overflow-hidden origin-top-left"></span>
                        </span>
                      </li>
                    ))}
                </ul>
              </div>
              <div className="!col-span-6 py-5">
                <div className="w-[420px] h-[420px] rounded-full border-1 border-[#0E6AED] mx-auto relative z-10 p-3.5">
                  <div className="w-full h-full bg-[#2437BF] rounded-full border-18 border-[#2437BF]">
                    {image && (
                      <GatsbyImage
                        image={image?.gatsbyImageData}
                        alt={image?.alt || "Xometry Resources"}
                        loading="lazy"
                        className="rounded-full overflow-hidden [&_img]:!object-cover [&_img]:!aspect-square z-10"
                      />
                    )}
                    {primary?.image && (
                      <GatsbyImage
                        image={primary.image?.gatsbyImageData}
                        alt={primary.image?.alt || "Xometry Resources"}
                        loading="lazy"
                        className="!absolute top-8 left-8 right-8 bottom-8 rounded-full overflow-hidden [&_img]:!object-cover [&_img]:!aspect-square"
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className="!col-span-3">
                <ul className="!grid text-center min-h-[460px]">
                  {rightCol &&
                    rightCol?.map((item, index) => (
                      <li
                        key={index}
                        className={clsx([
                          "!grid h-[115px] w-full grow",
                          item.css_class,
                        ])}
                      >
                        <a
                          className="text-lg underline self-center rounded-full text-[#476175] hover:!bg-[#13BF87] hover:text-white py-2 px-8 w-full"
                          href={item.cta_link?.url || ""}
                          onMouseEnter={() =>
                            item.image && handleMouseEnter(item.image)
                          }
                          onMouseLeave={handleMouseLeave}
                        >
                          {item.cta_text}
                        </a>
                        <span className="pointer absolute">
                          <span className="dot rounded-full block w-3.5 h-3.5 bg-[#0E6AED]"></span>
                          <span className="line absolute top-[7px] right-[7px] inline-block h-1 w-[330px] border-t-1 border-[#0E6AED] overflow-hidden origin-top-right"></span>
                        </span>
                      </li>
                    ))}
                </ul>
              </div>
            </div>
            <ul className="!grid text-center relative mt-18">
              {bottomRow &&
                bottomRow.map((item, index) => (
                  <li key={index} className={item.css_class || ""}>
                    <a
                      className="inline-block text-lg underline self-center rounded-full text-[#476175] hover:!bg-[#13BF87] hover:text-white py-2 px-8"
                      href={item.cta_link?.url || ""}
                      onMouseEnter={() =>
                        item.image && handleMouseEnter(item.image)
                      }
                      onMouseLeave={handleMouseLeave}
                    >
                      {item.cta_text}
                    </a>
                    <span className="pointer absolute -translate-x-[50%] -translate-y-[2rem]">
                      <span className="dot rounded-full block w-3.5 h-3.5 bg-[#0E6AED]"></span>
                      <span className="line absolute top-[7px] left-[7px] inline-block h-1 w-[330px] border-t-1 border-[#0E6AED] overflow-hidden origin-top-left"></span>
                    </span>
                  </li>
                ))}
            </ul>
          </div>
        </div>
      </section>
    )) || <></>
  );
};
export default Capabilities;
